import React, { useEffect, useMemo, useState } from "react";
import { getCandidates, getDashboard, getJobOpenings } from "../api/recruitmentApi";
import { normalizeList } from "./hrUtils";
import "./HRShared.css";
import "./RecruitmentReports.css";

const RecruitmentReports = () => {
  const [dashboard, setDashboard] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getDashboard(), getCandidates({ limit: 1000 }), getJobOpenings({ limit: 1000 })])
      .then(([dashboardResponse, candidateResponse, openingResponse]) => {
        setDashboard(dashboardResponse?.data?.data || dashboardResponse?.data || {});
        setCandidates(normalizeList(candidateResponse));
        setOpenings(normalizeList(openingResponse));
      })
      .catch((requestError) => setError(requestError.message));
  }, []);

  const sourceData = useMemo(() => {
    const counts = candidates.reduce((acc, item) => {
      const key = item.source || "Other";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).sort((a,b) => b[1]-a[1]);
  }, [candidates]);

  const departmentData = useMemo(() => {
    const counts = openings.reduce((acc, item) => {
      const key = item.department || "Other";
      acc[key] = (acc[key] || 0) + Number(item.numberOfPositions || 0);
      return acc;
    }, {});
    return Object.entries(counts).sort((a,b) => b[1]-a[1]);
  }, [openings]);

  const maxSource = Math.max(1, ...sourceData.map(([,value]) => value));
  const maxDepartment = Math.max(1, ...departmentData.map(([,value]) => value));

  return (
    <div className="hr-page">
      <header className="hr-page-header">
        <div><h1>Recruitment Reports</h1><p>Department demand, candidate sources and hiring performance.</p></div>
        <button className="hr-btn hr-btn-secondary" onClick={() => window.print()}>Print Report</button>
      </header>
      {error && <div className="hr-error">{error}</div>}
      <section className="hr-kpi-grid">
        <article className="hr-card hr-kpi"><div className="hr-kpi-label">Average Hiring Days</div><div className="hr-kpi-value">{dashboard.averageHiringDays || 0}</div></article>
        <article className="hr-card hr-kpi"><div className="hr-kpi-label">Offer Acceptance</div><div className="hr-kpi-value">{dashboard.offerAcceptanceRate || 0}%</div></article>
        <article className="hr-card hr-kpi"><div className="hr-kpi-label">Joining Rate</div><div className="hr-kpi-value">{dashboard.joiningRate || 0}%</div></article>
        <article className="hr-card hr-kpi"><div className="hr-kpi-label">Candidates per Hire</div><div className="hr-kpi-value">{dashboard.candidatesPerHire || 0}</div></article>
      </section>
      <section className="hr-two-column">
        <article className="hr-card hr-panel">
          <h2 className="hr-panel-title">Candidate Sources</h2>
          <div className="hr-report-list">
            {sourceData.map(([label,value]) => <div className="hr-report-row" key={label}><div className="hr-report-row-head"><span>{label}</span><strong>{value}</strong></div><div className="hr-report-track"><div className="hr-report-fill" style={{width:`${(value/maxSource)*100}%`}}/></div></div>)}
            {!sourceData.length && <div className="hr-empty">No candidate source data.</div>}
          </div>
        </article>
        <article className="hr-card hr-panel">
          <h2 className="hr-panel-title">Department-wise Hiring Demand</h2>
          <div className="hr-report-list">
            {departmentData.map(([label,value]) => <div className="hr-report-row" key={label}><div className="hr-report-row-head"><span>{label}</span><strong>{value}</strong></div><div className="hr-report-track"><div className="hr-report-fill" style={{width:`${(value/maxDepartment)*100}%`}}/></div></div>)}
            {!departmentData.length && <div className="hr-empty">No department data.</div>}
          </div>
        </article>
      </section>
    </div>
  );
};

export default RecruitmentReports;
